// Authed landing. Most owners have one boat, so /dashboard is a router, not a
// destination: one boat -> redirect straight into its dashboard; several boats -> a
// switcher; none -> the pairing hint. The old "Your boats" list is demoted to
// the multi-boat switcher.

import { Link, Navigate } from 'react-router-dom';

import { presenceDotClass } from '../components/app/boatChrome';
import { BoatFirmwareBanner } from '../components/app/BoatFirmwareBanner';
import { BoatUpdateBanner } from '../components/app/BoatUpdateBanner';
import { ChevronRightIcon } from '../components/app/icons';
import { Icon } from '../components/ui/Icon';
import { formatRelative, freshnessClass } from '../lib/deviceSeen';
import { useBoats } from '../lib/useBoats';
import { useDocumentTitle } from '../lib/useDocumentTitle';

export function Dashboard() {
  useDocumentTitle('Dashboard - Neomaris');
  const { state, refresh } = useBoats();

  if (state.kind === 'loading') {
    return <p className="dashboard-muted">Loading…</p>;
  }
  if (state.kind === 'error') {
    return (
      <ul className="form-errors">
        <li>{state.message}</li>
      </ul>
    );
  }

  const boats = state.boats;

  if (boats.length === 0) {
    return (
      <section className="dashboard">
        <header className="dashboard-head">
          <h2>Your boats</h2>
        </header>
        <div className="dashboard-empty tb-empty">
          <span className="tb-empty__icon" aria-hidden="true">
            <Icon name="anchor" size={26} />
          </span>
          <p className="tb-empty__title">No boats yet.</p>
          <p className="dashboard-muted">
            Pair one in the boat-side first-run UI — it will appear here automatically.
          </p>
        </div>
      </section>
    );
  }

  const onlyBoat = boats[0];
  if (boats.length === 1 && onlyBoat) {
    return <Navigate to={`/dashboard/boats/${onlyBoat.id}`} replace />;
  }

  return (
    <section data-testid="fleet">
      <div className="page-head">
        <h1>Your boats</h1>
        <p className="page-sub">
          Vessels paired with this account — select one to open its dashboard.
        </p>
      </div>
      <div className="fleet-grid">
        {boats.map((b) => {
          const state2 = b.last_contacted_at ? freshnessClass(b.last_contacted_at) : 'cold';
          const dot = presenceDotClass(
            state2 === 'fresh' ? 'fresh' : state2 === 'stale' ? 'stale' : 'cold'
          );
          return (
            <div className="fleet-card" key={b.id} data-testid={`boat-card-${b.id}`}>
              <div className="fleet-name-row">
                <span className={`boat-status-dot boat-status-dot--${dot}`} aria-hidden="true" />
                <span className="fleet-name">{b.display_name}</span>
              </div>
              <div className="fleet-last">Last contacted {formatRelative(b.last_contacted_at)}</div>
              {/* Just the version. The "update available" half of this line
                  moved into BoatUpdateBanner below, which is also where the
                  approval and its outcome live — saying it twice, once with a
                  button and once without, read as two different offers. */}
              <div className="fleet-last">
                {b.core_version ? `Core v${b.core_version}` : 'Core version unknown'}
                {b.firmware
                  ? ` · ${b.firmware.version ? `NeOS ${b.firmware.version}` : 'NeOS version unknown'}`
                  : ''}
              </div>
              {b.enforcementState === 'paused' ? (
                <div className="fleet-last" style={{ color: 'var(--status-bad-text)' }}>
                  Cloud sync paused — mandatory update required (local monitoring is unaffected).
                </div>
              ) : null}
              {b.enforcementState === 'follow_up' ? (
                <div className="fleet-last" style={{ color: 'var(--status-warn)' }}>
                  Update required — this core version can't update remotely; reinstall on the boat.
                </div>
              ) : null}
              <BoatUpdateBanner
                boat={b}
                coreLatest={b.coreUpdateAvailable ?? null}
                onRefresh={refresh}
              />
              <BoatFirmwareBanner key={b.id} boat={b} onRefresh={refresh} />
              <Link className="fleet-enter" to={`/dashboard/boats/${b.id}`}>
                Open dashboard
                <ChevronRightIcon />
              </Link>
            </div>
          );
        })}
      </div>
    </section>
  );
}
